"use client";

// Páginas de RH (Trabalhe conosco) consumindo /site/rh/*.
//
// Endpoints relevantes:
//   GET  /site/rh/vagas            -> listagem
//   GET  /site/rh/vagas/{slug}     -> detalhe
//   GET  /site/rh/form-fields      -> esquema do formulário dinâmico
//   GET  /site/rh/termo            -> termo de privacidade (LGPD, exigido)
//   POST /api/rh/candidaturas      -> proxy local pro /site/rh/candidaturas
//                                     (mantém X-Site-Api-Key server-side).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useLangContext } from "../lib/lang-context";
import { withLocale } from "../lib/i18n";
import Markdown from "./markdown";
import { BackLink } from "./mbm-components";

// Sentinela usado pelos chips de filtro pra representar "qualquer valor"
// sem colidir com strings vindas do CMS.
const RH_ALL = "__ALL__";

// Fallbacks locais do controle de upload e do eco da data — `t()` devolve ""
// pra chave ausente e o blob de strings vem do CMS, que ainda não define
// essas chaves.
const FILE_CHOOSE = {
  pt: "Escolher arquivo",
  en: "Choose file",
  es: "Elegir archivo",
};
const FILE_NONE = {
  pt: "Nenhum arquivo selecionado",
  en: "No file chosen",
  es: "Ningún archivo seleccionado",
};
const FILE_TOO_BIG = {
  pt: "Arquivo muito grande ({size} MB). Tamanho máximo: {max} MB.",
  en: "File is too large ({size} MB). Maximum size: {max} MB.",
  es: "Archivo demasiado grande ({size} MB). Tamaño máximo: {max} MB.",
};
const FILE_MAX = {
  pt: "Tamanho máximo: {max} MB",
  en: "Maximum size: {max} MB",
  es: "Tamaño máximo: {max} MB",
};
const DATE_PICKED = {
  pt: "Data selecionada:",
  en: "Selected date:",
  es: "Fecha seleccionada:",
};

// Strip horizontal de chips com os atributos da vaga (Área / Nível /
// Regime / Local). Cada chip só renderiza se o campo existir na vaga —
// vagas legadas sem esses campos seguem funcionando sem mudança.
function VagaChips({ vaga, withLocal = true }) {
  const items = [
    vaga?.area && { k: "area", v: vaga.area },
    vaga?.nivel && { k: "nivel", v: vaga.nivel },
    vaga?.regime && { k: "regime", v: vaga.regime },
    withLocal && vaga?.local && { k: "local", v: vaga.local },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="rh-chips">
      {items.map((it) => (
        <span key={it.k} className={`rh-chip rh-chip-${it.k}`}>{it.v}</span>
      ))}
    </div>
  );
}

function localeTag(lang) {
  return lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR";
}

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(localeTag(lang), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// `<input type="date">` desenha o placeholder e a ordem dos campos com a
// locale de UI do BROWSER, não com o `lang` do documento — Chrome ignora
// `lang` no elemento, no ancestral e no <html> (verificado: só muda com
// --lang na linha de comando do navegador). Num Chrome en-US o campo aparece
// como mm/dd/yyyy mesmo em página PT e não há atributo/CSS que corrija isso.
// Em vez de trocar por um picker próprio, ecoamos a data escolhida por
// extenso na locale da página, pra o usuário conferir o que o widget
// interpretou. Monta a Date por partes (sem parse ISO) pra não escorregar um
// dia por fuso horário.
function echoDate(value, lang) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(localeTag(lang), {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// O schema do CMS não traz autocomplete/inputmode, então derivamos do tipo do
// campo (e do fieldName pra "nome", que chega como TEXT).
const AUTOFILL_BY_TYPE = {
  EMAIL: { autoComplete: "email", inputMode: "email" },
  PHONE: { autoComplete: "tel", inputMode: "tel" },
};

// O form tem 800px+ e o aviso de sucesso ~140px: como o scroll é preservado na
// troca, quem enviou do fim do form fica olhando pro rodapé e lê isso como
// envio falhado.
function useScrollToSuccess(done) {
  const ref = useRef(null);
  useEffect(() => {
    if (!done) return;
    const reduz = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current?.scrollIntoView({
      behavior: reduz ? "auto" : "smooth",
      block: "center",
    });
  }, [done]);
  return ref;
}

function autofillProps(f) {
  const byType = AUTOFILL_BY_TYPE[f.type];
  if (byType) return byType;
  if (f.type === "TEXT" && /^(nome|name|full_?name)/i.test(f.fieldName ?? "")) {
    return { autoComplete: "name" };
  }
  return {};
}

// Controle de upload próprio. O botão e o "no file chosen" do
// `<input type="file">` nativo saem no idioma do browser e nenhum atributo ou
// CSS traduz isso, então o input fica visually-hidden (segue focável e no tab
// order, ao contrário de display:none) e um <label for> desenha o texto em PT
// + o nome do arquivo. Também aplica o limite de tamanho que o texto de ajuda
// já prometia mas nada checava.
function FileField({ field: f, onChange, onSizeError }) {
  const { t, lang } = useLangContext();
  const [picked, setPicked] = useState(null);
  const [tooBig, setTooBig] = useState(null);
  const uid = useId();
  const inputId = `${uid}-file`;
  const helpId = `${uid}-help`;
  const errId = `${uid}-err`;

  const accept =
    Array.isArray(f.fileConstraints?.mimes) && f.fileConstraints.mimes.length > 0
      ? f.fileConstraints.mimes.join(",")
      : "application/pdf,.pdf";
  const maxMB = f.fileConstraints?.maxSizeMB;

  const handleChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (file && maxMB && file.size > maxMB * 1024 * 1024) {
      setPicked(null);
      setTooBig((file.size / (1024 * 1024)).toFixed(1).replace(".", ","));
      onChange?.(null);
      onSizeError?.(true);
      e.target.value = "";
      return;
    }
    setPicked(file);
    setTooBig(null);
    onChange?.(file);
    onSizeError?.(false);
  };

  const describedBy =
    [maxMB ? helpId : null, tooBig ? errId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className="field rh-file-field">
      <label className="rh-file-legend" htmlFor={inputId}>
        {f.label}
        {f.required && <em aria-hidden="true"> *</em>}
      </label>
      <input
        id={inputId}
        className="visually-hidden rh-file-input"
        type="file"
        name={f.fieldName}
        required={f.required}
        accept={accept}
        aria-describedby={describedBy}
        aria-invalid={tooBig ? "true" : undefined}
        onChange={handleChange}
      />
      <div className="rh-file-control">
        <label className="rh-file-trigger" htmlFor={inputId}>
          {t("rh_file_choose") || FILE_CHOOSE[lang] || FILE_CHOOSE.pt}
        </label>
        <span className="rh-file-name" title={picked?.name || undefined}>
          {picked?.name || t("rh_file_none") || FILE_NONE[lang] || FILE_NONE.pt}
        </span>
      </div>
      {maxMB && (
        <small id={helpId} className="rh-field-help">
          {(t("rh_file_max") || FILE_MAX[lang] || FILE_MAX.pt).replace(
            "{max}",
            maxMB
          )}
        </small>
      )}
      {tooBig && (
        <small id={errId} className="rh-file-error" role="alert">
          {(t("rh_file_too_big") || FILE_TOO_BIG[lang] || FILE_TOO_BIG.pt)
            .replace("{size}", tooBig)
            .replace("{max}", maxMB)}
        </small>
      )}
    </div>
  );
}

// ───── Listagem ──────────────────────────────────────────────────────────────

export function VagasListPage({ vagas }) {
  const { t, lang } = useLangContext();
  const list = Array.isArray(vagas) ? vagas : [];

  // Filtros locais (Área / Nível / Regime). Mantidos só em state — não
  // sobem pra query string pra não competir com a navegação i18n existente.
  const [fArea, setFArea] = useState(RH_ALL);
  const [fNivel, setFNivel] = useState(RH_ALL);
  const [fRegime, setFRegime] = useState(RH_ALL);

  // Listas de opções derivadas das próprias vagas. Vagas legadas (sem
  // area/nivel/regime) simplesmente não contribuem com chips — o filtro
  // some quando não tem opções.
  const areas = useMemo(
    () => Array.from(new Set(list.map((v) => v.area).filter(Boolean))),
    [list]
  );
  const niveis = useMemo(
    () => Array.from(new Set(list.map((v) => v.nivel).filter(Boolean))),
    [list]
  );
  const regimes = useMemo(
    () => Array.from(new Set(list.map((v) => v.regime).filter(Boolean))),
    [list]
  );

  const filtered = list.filter(
    (v) =>
      (fArea === RH_ALL || v.area === fArea) &&
      (fNivel === RH_ALL || v.nivel === fNivel) &&
      (fRegime === RH_ALL || v.regime === fRegime)
  );

  const hasAnyFilter =
    fArea !== RH_ALL || fNivel !== RH_ALL || fRegime !== RH_ALL;
  const clearFilters = () => {
    setFArea(RH_ALL);
    setFNivel(RH_ALL);
    setFRegime(RH_ALL);
  };

  const ChipBar = ({ label, value, options, onPick }) =>
    options.length === 0 ? null : (
      <div className="rh-filter-group">
        <span className="rh-filter-label">{label}</span>
        <button
          type="button"
          className={"chip " + (value === RH_ALL ? "on" : "")}
          onClick={() => onPick(RH_ALL)}
        >
          {t("rh_filter_all") || "Todas"}
        </button>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            className={"chip " + (value === o ? "on" : "")}
            onClick={() => onPick(o)}
          >
            {o}
          </button>
        ))}
      </div>
    );

  return (
    <div className="page">
      <section className="hero shell">
        <div className="meta-row">
          <span>{t("rh_kicker") || "@rh"}</span>
          <span>
            {list.length} {String(t("rh_open_label") || "vagas abertas").toUpperCase()}
          </span>
        </div>
        <h1 style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
          {t("rh_title") || "Trabalhe conosco"}
        </h1>
        <p className="sub" style={{ maxWidth: "64ch" }}>
          {t("rh_intro") ||
            "Se você quer fazer parte do nosso time, confira as vagas abaixo e candidate-se."}
        </p>
      </section>

      <section className="section shell">
        {list.length === 0 ? (
          <p className="rh-empty">
            {t("rh_empty") ||
              "No momento não temos vagas abertas. Volte em breve!"}
          </p>
        ) : (
          <>
            {(areas.length > 0 || niveis.length > 0 || regimes.length > 0) && (
              <div className="rh-filters">
                <ChipBar
                  label={t("rh_filter_area") || "Área"}
                  value={fArea}
                  options={areas}
                  onPick={setFArea}
                />
                <ChipBar
                  label={t("rh_filter_nivel") || "Nível"}
                  value={fNivel}
                  options={niveis}
                  onPick={setFNivel}
                />
                <ChipBar
                  label={t("rh_filter_regime") || "Regime"}
                  value={fRegime}
                  options={regimes}
                  onPick={setFRegime}
                />
              </div>
            )}

            <div className="label-num" style={{ margin: "0 0 16px" }}>
              {filtered.length}{" "}
              {String(t("rh_open_label") || "vagas abertas")}
            </div>

            <ul className="rh-list">
              {filtered.map((v) => (
                <li key={v.id} className="rh-list-item">
                  <Link
                    href={withLocale(`/trabalhe-conosco/${v.slug}`, lang)}
                    className="rh-list-link"
                  >
                    <div className="rh-list-body">
                      <h3 className="rh-list-title">{v.titulo}</h3>
                      <VagaChips vaga={v} />
                      {v.dataPublicacao && (
                        <p className="rh-list-meta">
                          {t("rh_published_at") || "Publicada em"}{" "}
                          {formatDate(v.dataPublicacao, lang)}
                        </p>
                      )}
                    </div>
                    <span className="rh-list-arrow">→</span>
                  </Link>
                </li>
              ))}
            </ul>

            {filtered.length === 0 && hasAnyFilter && (
              <p className="rh-empty" style={{ marginTop: 24 }}>
                {t("rh_no_match") ||
                  "Nenhuma vaga com esses filtros."}{" "}
                <button type="button" className="link" onClick={clearFilters}>
                  {t("rh_clear_filters") || "Limpar filtros"}
                </button>
                .
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ───── Detalhe + formulário ──────────────────────────────────────────────────

export function VagaDetailPage({ vaga, fields, termo }) {
  const { t, lang } = useLangContext();
  const fieldsList = Array.isArray(fields)
    ? [...fields].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
    : [];

  return (
    <div className="page">
      <section className="hero shell">
        <div className="meta-row">
          <BackLink
            href={withLocale("/trabalhe-conosco", lang)}
            kind="jobs"
            lang={lang}
            label={t("rh_back_to_list")}
          />
          {vaga?.dataPublicacao && (
            <span>{formatDate(vaga.dataPublicacao, lang)}</span>
          )}
        </div>
        <h1 style={{ fontSize: "clamp(40px, 6vw, 72px)" }}>{vaga?.titulo}</h1>
        <VagaChips vaga={vaga} />
      </section>

      <section className="section shell rh-detail-grid">
        <div className="rh-detail-content">
          {vaga?.descricao && (
            <>
              <h2 className="rh-detail-section-title">
                {t("rh_section_description") || "Descrição da vaga"}
              </h2>
              <Markdown source={vaga.descricao} />
            </>
          )}
          {vaga?.requisitos && (
            <>
              <h2 className="rh-detail-section-title">
                {t("rh_section_requirements") || "Requisitos"}
              </h2>
              <Markdown source={vaga.requisitos} />
            </>
          )}
        </div>

        <aside className="rh-detail-form">
          <h2 className="rh-detail-section-title">
            {t("rh_section_apply") || "Candidatar-se"}
          </h2>
          {termo ? (
            <CandidaturaForm
              vagaSlug={vaga.slug}
              fields={fieldsList}
              termo={termo}
            />
          ) : (
            <p className="rh-form-disabled">
              {t("rh_form_unavailable") ||
                "Formulário em manutenção. Tente novamente mais tarde."}
            </p>
          )}
        </aside>
      </section>
    </div>
  );
}

// ───── Formulário ────────────────────────────────────────────────────────────

function CandidaturaForm({ vagaSlug, fields, termo }) {
  const { t } = useLangContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [termoOpen, setTermoOpen] = useState(false);
  const [fileTooBig, setFileTooBig] = useState(false);
  const successRef = useScrollToSuccess(done);

  const fileField = fields.find((f) => f.type === "FILE");

  async function handleSubmit(event) {
    event.preventDefault();
    // O arquivo acima do limite já foi descartado no campo, que mostra o erro
    // inline — aqui só barramos o envio.
    if (fileTooBig) return;
    setSubmitting(true);
    setError(null);

    const formEl = event.currentTarget;
    const form = new FormData(formEl);

    // Monta payload de campos não-FILE conforme tipo.
    const payload = {};
    for (const f of fields) {
      if (f.type === "FILE") continue;
      if (f.type === "CHECKBOX") {
        payload[f.fieldName] = form.get(f.fieldName) === "on";
      } else if (f.type === "CHECKBOX_GROUP") {
        payload[f.fieldName] = form.getAll(f.fieldName);
      } else {
        payload[f.fieldName] = String(form.get(f.fieldName) ?? "");
      }
    }

    const fd = new FormData();
    fd.append("vagaSlug", vagaSlug);
    fd.append("payload", JSON.stringify(payload));
    fd.append("termoAceito", form.get("__termo") === "on" ? "true" : "false");
    if (fileField) {
      const file = form.get(fileField.fieldName);
      if (file && typeof file === "object" && file.size > 0) {
        fd.append("curriculo", file);
      }
    }

    try {
      const res = await fetch("/api/rh/candidaturas", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error(
          data?.error ||
            data?.message ||
            (t("rh_form_error_generic") || "Erro ao enviar candidatura.")
        );
      }
      setDone(true);
      formEl.reset();
    } catch (err) {
      setError(err?.message || (t("rh_form_error_generic") || "Erro."));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rh-form-success" ref={successRef} role="status">
        <h3>{t("rh_form_success_title") || "Candidatura recebida!"}</h3>
        <p>
          {t("rh_form_success_desc") ||
            "Obrigado pelo interesse. Em breve nossa equipe entrará em contato."}
        </p>
      </div>
    );
  }

  return (
    <form className="rh-form form-wrap" onSubmit={handleSubmit} noValidate>
      <div className="rh-form-fields">
        {fields.map((f) => (
          <FieldRenderer key={f.id} field={f} onSizeError={setFileTooBig} />
        ))}
      </div>

      <div className="rh-form-termo">
        <label className="rh-form-checkbox">
          <input type="checkbox" name="__termo" required />
          <span>
            {t("rh_form_accept_prefix") || "Li e aceito o"}{" "}
            <button
              type="button"
              className="link rh-form-termo-toggle"
              onClick={() => setTermoOpen((v) => !v)}
              aria-expanded={termoOpen}
            >
              {t("rh_form_accept_link") || "termo de privacidade"}
            </button>
            .
          </span>
        </label>
        {termoOpen && (
          <div className="rh-form-termo-content">
            <Markdown source={termo?.conteudo ?? ""} />
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="rh-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="cta-btn rh-form-submit"
        disabled={submitting || fileTooBig}
      >
        {submitting
          ? t("rh_form_submitting") || "Enviando…"
          : t("rh_form_submit") || "Enviar candidatura"}
        <span className="arr" aria-hidden="true">→</span>
      </button>
    </form>
  );
}

// ───── Render de campos por tipo ─────────────────────────────────────────────

const SIMPLE_INPUT_TYPES = {
  TEXT: "text",
  EMAIL: "email",
  PHONE: "tel",
  NUMBER: "number",
  DATE: "date",
};

function FieldRenderer({ field: f, onSizeError }) {
  if (f.type === "TEXTAREA") {
    return (
      <label className="field">
        <span>
          {f.label}
          {f.required && <em aria-hidden="true"> *</em>}
        </span>
        <textarea name={f.fieldName} required={f.required} rows={4} />
      </label>
    );
  }
  if (f.type === "SELECT") {
    return (
      <label className="field">
        <span>
          {f.label}
          {f.required && <em aria-hidden="true"> *</em>}
        </span>
        <select name={f.fieldName} required={f.required} defaultValue="">
          <option value="" disabled>
            —
          </option>
          {(f.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (f.type === "RADIO") {
    return (
      <fieldset className="field rh-field-group">
        <legend>
          {f.label}
          {f.required && <em aria-hidden="true"> *</em>}
        </legend>
        <div className="rh-field-options">
          {(f.options ?? []).map((opt) => (
            <label key={opt.value} className="rh-field-option">
              <input
                type="radio"
                name={f.fieldName}
                value={opt.value}
                required={f.required}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (f.type === "CHECKBOX") {
    return (
      <label className="field rh-form-checkbox">
        <input type="checkbox" name={f.fieldName} />
        <span>{f.label}</span>
      </label>
    );
  }
  if (f.type === "CHECKBOX_GROUP") {
    return (
      <fieldset className="field rh-field-group">
        <legend>
          {f.label}
          {f.required && <em aria-hidden="true"> *</em>}
        </legend>
        <div className="rh-field-options">
          {(f.options ?? []).map((opt) => (
            <label key={opt.value} className="rh-field-option">
              <input type="checkbox" name={f.fieldName} value={opt.value} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (f.type === "FILE") {
    return <FileField field={f} onSizeError={onSizeError} />;
  }

  const inputType = SIMPLE_INPUT_TYPES[f.type] || "text";
  return (
    <label className="field">
      <span>
        {f.label}
        {f.required && <em aria-hidden="true"> *</em>}
      </span>
      <input
        type={inputType}
        name={f.fieldName}
        required={f.required}
        {...autofillProps(f)}
      />
    </label>
  );
}

// ─── Trabalhe Conosco — formulário sempre aberto (sem vaga) ──────────────────
//
// Consome GET /site/rh/form-fields + GET /site/rh/termo e submete em
// POST /api/rh/trabalhe-conosco (proxy local pro /site/rh/trabalhe-conosco).
// Diferenças vs CandidaturaForm acima:
//   - Sem vagaSlug no payload (candidatura espontânea).
//   - Inputs controlados (state-driven) pra suportar showWhen condicional.
//   - Campo só renderiza quando showWhen.field === showWhen.equals; o backend
//     respeita a mesma regra, então campo oculto é omitido do payload.

export function TrabalheConoscoPage({ fields, termo }) {
  const { t } = useLangContext();
  const list = Array.isArray(fields) ? fields : [];

  return (
    <div className="page">
      <section className="hero shell">
        <h1 style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
          {t("rh_title") || "Trabalhe conosco"}
        </h1>
        <p className="sub" style={{ maxWidth: "64ch" }}>
          {t("rh_intro_static") ||
            "Sempre estamos buscando talentos. Preencha o formulário e nosso time entrará em contato quando surgir uma oportunidade compatível."}
        </p>
      </section>

      <section className="section shell">
        {!termo ? (
          <p className="rh-form-disabled">
            {t("rh_form_unavailable") ||
              "Formulário temporariamente indisponível. Tente novamente mais tarde."}
          </p>
        ) : list.length === 0 ? (
          <p className="rh-empty">
            {t("rh_empty") ||
              "Formulário ainda não configurado. Volte em breve!"}
          </p>
        ) : (
          <TrabalheConoscoForm fields={list} termo={termo} />
        )}
      </section>
    </div>
  );
}

function TrabalheConoscoForm({ fields, termo }) {
  const { t } = useLangContext();
  const [values, setValues] = useState({});
  const [fileValue, setFileValue] = useState(null);
  const [fileTooBig, setFileTooBig] = useState(false);
  const [termoAceito, setTermoAceito] = useState(false);
  const [termoOpen, setTermoOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const successRef = useScrollToSuccess(done);

  const ordered = useMemo(
    () => [...fields].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [fields]
  );

  // Regra showWhen: o campo aparece quando o gatilho tem exatamente o valor
  // esperado (case-insensitive). Sem gatilho preenchido, esconde.
  const isVisible = (field) => {
    if (!field.showWhen) return true;
    const v = values[field.showWhen.field];
    if (v === null || v === undefined) return false;
    return (
      String(v).trim().toLowerCase() ===
      String(field.showWhen.equals).trim().toLowerCase()
    );
  };

  const setValue = (fieldName, v) => {
    setValues((cur) => ({ ...cur, [fieldName]: v }));
  };

  async function handleSubmit(event) {
    event.preventDefault();
    // O arquivo acima do limite já foi descartado no campo, que mostra o erro
    // inline — aqui só barramos o envio.
    if (fileTooBig) return;
    if (!termoAceito) {
      setError(
        t("rh_form_termo_required") ||
          "É necessário aceitar o termo de privacidade."
      );
      return;
    }
    setSubmitting(true);
    setError(null);

    // Payload: só campos visíveis e não-FILE. CHECKBOX vira boolean,
    // CHECKBOX_GROUP vira array, resto string.
    const payload = {};
    for (const f of ordered) {
      if (f.type === "FILE") continue;
      if (!isVisible(f)) continue;
      if (f.type === "CHECKBOX") {
        payload[f.fieldName] = Boolean(values[f.fieldName]);
      } else if (f.type === "CHECKBOX_GROUP") {
        payload[f.fieldName] = Array.isArray(values[f.fieldName])
          ? values[f.fieldName]
          : [];
      } else {
        payload[f.fieldName] = String(values[f.fieldName] ?? "");
      }
    }

    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload));
    fd.append("termoAceito", "true");
    const fileField = ordered.find((f) => f.type === "FILE" && isVisible(f));
    if (fileField && fileValue) {
      fd.append("curriculo", fileValue);
    }

    try {
      const res = await fetch("/api/rh/trabalhe-conosco", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error(
          data?.error ||
            data?.mensagem ||
            data?.message ||
            (t("rh_form_error_generic") || "Erro ao enviar candidatura.")
        );
      }
      setDone(true);
      setValues({});
      setFileValue(null);
      setTermoAceito(false);
    } catch (err) {
      setError(err?.message || (t("rh_form_error_generic") || "Erro."));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rh-form-success" ref={successRef} role="status">
        <h3>{t("rh_form_success_title") || "Candidatura recebida!"}</h3>
        <p>
          {t("rh_form_success_desc") ||
            "Obrigado pelo interesse. Em breve nossa equipe entrará em contato."}
        </p>
      </div>
    );
  }

  return (
    <form className="rh-form form-wrap" onSubmit={handleSubmit} noValidate>
      <div className="rh-form-fields">
        {ordered.map((f) => {
          if (!isVisible(f)) return null;
          return (
            <ControlledFieldRenderer
              key={f.id}
              field={f}
              value={f.type === "FILE" ? fileValue : values[f.fieldName] ?? ""}
              onChange={(v) => {
                if (f.type === "FILE") setFileValue(v);
                else setValue(f.fieldName, v);
              }}
              onSizeError={setFileTooBig}
            />
          );
        })}
      </div>

      <div className="rh-form-termo">
        <label className="rh-form-checkbox">
          <input
            type="checkbox"
            checked={termoAceito}
            onChange={(e) => setTermoAceito(e.target.checked)}
          />
          <span>
            {t("rh_form_accept_prefix") || "Li e aceito o"}{" "}
            <button
              type="button"
              className="link rh-form-termo-toggle"
              onClick={() => setTermoOpen((v) => !v)}
              aria-expanded={termoOpen}
            >
              {t("rh_form_accept_link") || "termo de privacidade"}
            </button>
            .
          </span>
        </label>
        {termoOpen && (
          <div className="rh-form-termo-content">
            <Markdown source={termo?.conteudo ?? ""} />
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="rh-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="cta-btn rh-form-submit"
        disabled={submitting || !termoAceito || fileTooBig}
      >
        {submitting
          ? t("rh_form_submitting") || "Enviando…"
          : t("rh_form_submit") || "Enviar candidatura"}
        <span className="arr" aria-hidden="true">→</span>
      </button>
    </form>
  );
}

// Variante state-driven do FieldRenderer pra suportar inputs controlados
// (necessário pra avaliação reativa do showWhen). Mesmo switch por f.type,
// mas com value/onChange ao invés de FormData read-on-submit.
function ControlledFieldRenderer({ field: f, value, onChange, onSizeError }) {
  const { t, lang } = useLangContext();
  const reqStar = f.required ? <em aria-hidden="true"> *</em> : null;

  if (f.type === "TEXTAREA") {
    return (
      <label className="field">
        <span>
          {f.label}
          {reqStar}
        </span>
        <textarea
          name={f.fieldName}
          required={f.required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }
  if (f.type === "SELECT") {
    return (
      <label className="field">
        <span>
          {f.label}
          {reqStar}
        </span>
        <select
          name={f.fieldName}
          required={f.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>
            —
          </option>
          {(f.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (f.type === "RADIO") {
    return (
      <fieldset className="field rh-field-group">
        <legend>
          {f.label}
          {reqStar}
        </legend>
        <div className="rh-field-options">
          {(f.options ?? []).map((opt) => (
            <label key={opt.value} className="rh-field-option">
              <input
                type="radio"
                name={f.fieldName}
                value={opt.value}
                required={f.required}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (f.type === "CHECKBOX") {
    return (
      <label className="field rh-form-checkbox">
        <input
          type="checkbox"
          name={f.fieldName}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{f.label}</span>
      </label>
    );
  }
  if (f.type === "CHECKBOX_GROUP") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <fieldset className="field rh-field-group">
        <legend>
          {f.label}
          {reqStar}
        </legend>
        <div className="rh-field-options">
          {(f.options ?? []).map((opt) => (
            <label key={opt.value} className="rh-field-option">
              <input
                type="checkbox"
                name={f.fieldName}
                value={opt.value}
                checked={arr.includes(opt.value)}
                onChange={(e) => {
                  const set = new Set(arr);
                  if (e.target.checked) set.add(opt.value);
                  else set.delete(opt.value);
                  onChange(Array.from(set));
                }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (f.type === "FILE") {
    return <FileField field={f} onChange={onChange} onSizeError={onSizeError} />;
  }

  const inputType = SIMPLE_INPUT_TYPES[f.type] || "text";
  const echo = f.type === "DATE" ? echoDate(value, lang) : "";
  return (
    <label className="field">
      <span>
        {f.label}
        {reqStar}
      </span>
      <input
        type={inputType}
        name={f.fieldName}
        required={f.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...autofillProps(f)}
      />
      {echo && (
        <small className="rh-field-help">
          {t("rh_date_picked") || DATE_PICKED[lang] || DATE_PICKED.pt} {echo}
        </small>
      )}
    </label>
  );
}
